import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  ConsoleItems,
  ConsoleItemsQuery,
  ConsoleItemsQueryVariables,
  ConsoleMissionBoxFragmentDoc,
  ConsoleMissionEditQuery,
  ConsoleMissionEditQueryVariables,
  UpdateMissionMutation,
  UpdateMissionMutationVariables,
} from '@/clients/utoniq-core/schema'
import { buildGpsData } from '@/components/pages/ConsoleMissionCreate/viewModel'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { useLocationWatcher } from '@/core/native/geolocation'
import {
  getDetail,
  getPlacePredictions,
  Place,
  Prediction,
} from '@/lib/googleMap'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleMissionEdit')
  const { showError, showMessage } = useMessage()
  const { user } = useCurrentUser()
  const { router, routerPages } = useRouter<{ missionId: string }>()
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [place, setPlace] = useState<Place | null>(null)
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')
  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  const { data } = useQuery<
    ConsoleMissionEditQuery,
    ConsoleMissionEditQueryVariables
  >(QUERY, { variables: { id: router.query.missionId } })

  const [getItems, { data: items, loading }] = useLazyQuery<
    ConsoleItemsQuery,
    ConsoleItemsQueryVariables
  >(ConsoleItems)

  const [updateMission] = useMutation<
    UpdateMissionMutation,
    UpdateMissionMutationVariables
  >(UPDATE_MISSION, {
    onCompleted() {
      showMessage(t('editMissionComplete'), 'success')
      router.goBack(routerPages.ConsoleMissions)
    },
  })

  const getResource = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit['image']) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  useEffect(() => {
    if (!data?.mission?.gpsData) return
    requestPermission().catch(showError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.mission?.gpsData, requestPermission])

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, unwatch, permission])

  return {
    state: {
      artistId: user?.uid ?? '',
      mission: data?.mission ?? null,
      progress,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
      items: items?.artistItems ?? null,
      predictions,
      place,
      hasNextPage: !!items?.artistItems?.pageInfo.hasNextPage,
      loading,
    },
    actions: {
      async onSubmit({
        title,
        description,
        type,
        gpsData,
        content,
        reward,
        startedAt,
        expiredAt,
      }) {
        await updateMission({
          variables: {
            id: router.query.missionId,
            inputs: {
              title,
              description,
              type,
              isPublished: true,
              startedAt: startedAt,
              expiredAt: expiredAt,
              content,
              reward,
              gpsData: buildGpsData(gpsData),
            },
          },
        })
      },
      async onDrop() {
        const file = await getResource().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onSearch() {
        await getItems()
      },
      async onFetchMore() {
        await getItems({
          variables: {
            pageInput: { cursor: items?.artistItems?.pageInfo.endCursor },
          },
        })
      },
      async onPlaceSearch({ keyword }) {
        if (!keyword) {
          return
        }
        const predictions = await getPlacePredictions(keyword)
        setPredictions(predictions)
      },
      async onSelectPlace({ placeId, map }) {
        const result = await getDetail({ placeId, map })
        if (result.isErr()) {
          throw result.err()
        }
        setPlace(result.unwrap())
      },
      removePlace() {
        setPlace(null)
      },
      async onRequestGPSPermission() {
        await requestPermission()
      },
    },
  }
})

const QUERY = gql`
  query ConsoleMissionEdit($id: ID!) {
    mission(id: $id) {
      ...ConsoleMissionBox
    }
  }
  ${ConsoleMissionBoxFragmentDoc}
`

const UPDATE_MISSION = gql`
  mutation UpdateMission($id: ID!, $inputs: UpdateMissionInput!) {
    updateMission(id: $id, inputs: $inputs) {
      ...ConsoleMissionBox
    }
  }
  ${ConsoleMissionBoxFragmentDoc}
`
